import React, { useCallback } from 'react'
import cx from 'classnames'
import { subscribe } from 'react-contextual'
import { Helmet } from 'react-helmet'
import { useEffectOnce } from 'react-use'
import { DashboardProvider } from '../..'
import { isEmpty, path, pathOr } from 'ramda'
import useBreakpoints from '../../../../views/hooks/useBreakpoints'
import styles from './DashboardPage.module.scss'
import { useSelector, useDispatch } from 'react-redux'
import { walletApiSelectors, walletApiEvents } from '../../../api/wallet'
import { assets } from '../../../api/wallet/constants'
import { WithdrawFundsModal } from '../../../../views/components'
import { agencyApiSelectors } from '../../../api/agency'
import TrustAccounts from '../TrustAccounts/TrustAccounts'
import { toCents } from 'utils/currency'
import DashboardSummaryWidgets from './DashboardSummaryWidgets'
import DashboardPerformanceSummary from './DashboardPerformanceSummary'
import { userApiSelectors } from '../../../api/user'
import { reportingApiSelectors } from 'modules/api/reporting'

const DashboardPage = ({
  currentAgencyId,
  agencyName,
  agencyTrading,
  // agencyStatsChartDataForDays,
  openBankStatement,
  fetchDepositSummaryStats,
}: any) => {
  const agencyId = useSelector(userApiSelectors.getCurrentAgencyId)

  useEffectOnce(() => {
    if (agencyId) {
      // Single consolidated call: /reports/dashboard-summary-stats serves every
      // dashboard tile (deposit/transactional/commission balances, commission &
      // funds-collected 30-day flows, recon settled%/funds, and portfolio
      // counts), replacing the former fan-out to Phoenix /accounting/stats
      // (~7s), /portfolios/stats, /reconciliations/stats and the unused
      // deposit-bank-statement prefetch.
      fetchDepositSummaryStats()
    }
  })

  const { isMobile } = useBreakpoints()

  const classes = cx([styles.root])

  const dispatch = useDispatch()

  const agencyBankDetails = useSelector(agencyApiSelectors.getCurrentAgencyBankDetails)
  const globalBalance = useSelector(state => walletApiSelectors.getGlobalBalanceAsset(state)(currentAgencyId))
  const withdrawalModalOpen = useSelector(state => walletApiSelectors.getWithdrawalModalStatus(state)())
  const isOwner = useSelector(userApiSelectors.isOwnerRole)
  const depositSummaryStats = useSelector(state => reportingApiSelectors.getDepositSummaryStats(state))

  // Transactional Account balance now comes from the dashboard-summary-stats
  // endpoint (sum of the agency's Consolidated + Account + Global wallet
  // balances from wallet.wallet_balances), replacing the legacy Phoenix
  // bank-statement closing balance which had drifted (and could go negative).
  const transactionalAccountBalance = pathOr(0, ['TransactionalAccountBalance'], depositSummaryStats)
  // Commission Account = the agency's Global wallet, now served by the same
  // dashboard-summary-stats endpoint. The value is in rand → convert to cents
  // to match the tile/withdraw-modal contract that getGlobalBalance returned.
  // The endpoint only sums real ZAR, so a test agency funded with the dummy coin read R 0.00 here
  // while the drawer and payout (both on the wallet's preferred asset) saw its funds. Use the
  // wallet's dummy balance for those agencies; it is already in cents.
  const commissionAccountBalance =
    globalBalance && globalBalance.name === assets.dummy
      ? globalBalance.balance
      : toCents(pathOr(0, ['CommissionAccountBalance'], depositSummaryStats)).getAmount()

  const closeConfirmation = () => dispatch(walletApiEvents.withdrawalModalRequestClosed())

  const handlePayout = useCallback(() => {
    // `getGlobalBalanceAsset` is null/false until there is a wallet asset to pay out of.
    const asset = globalBalance ? globalBalance.name : undefined
    const amount = globalBalance ? globalBalance.balance : undefined
    if (currentAgencyId && asset && amount) {
      dispatch(
        walletApiEvents.payout_request({
          party: currentAgencyId,
          asset,
          amount,
        }),
      )
    }
  }, [dispatch, globalBalance, currentAgencyId])

  return (
    <>
      <Helmet>
        <title>reOS | Dashboard</title>
      </Helmet>
      <div className={classes}>
        {!isMobile && (
          <header>
            {isEmpty(agencyTrading) ? (
              <h1>{agencyName}</h1>
            ) : !isEmpty(agencyTrading) ? (
              <>
                <h1>{agencyTrading}</h1>
                <h2>{agencyName}</h2>
              </>
            ) : (
              <h1>{agencyName}</h1>
            )}
          </header>
        )}
        <div className={styles.body}>
          <section>
            <DashboardSummaryWidgets />
          </section>
          <section className={styles['chart-section']}>
            <DashboardPerformanceSummary />
          </section>
          <section>
            <h2>Trust accounts</h2>
            <TrustAccounts
              transactionalBalance={toCents(transactionalAccountBalance).getAmount()}
              depositBalance={toCents(pathOr('0', ['ClosingBalance'], depositSummaryStats)).getAmount()}
              commissionBalance={commissionAccountBalance}
              bankName={pathOr('', ['bank'], agencyBankDetails)}
              accountNumber={pathOr('', ['accountNumber'], agencyBankDetails)}
              canWithdraw={isOwner}
              viewStatementDisabled={!currentAgencyId}
              onViewStatement={openBankStatement}
              onWithdraw={() => dispatch(walletApiEvents.withdrawalModalRequestOpened())}
            />
          </section>
        </div>
      </div>
      <WithdrawFundsModal
        isOpen={withdrawalModalOpen}
        balance={commissionAccountBalance}
        bankName={pathOr('', ['bank'], agencyBankDetails)}
        number={pathOr('', ['accountNumber'], agencyBankDetails)}
        onClose={closeConfirmation}
        onCancel={closeConfirmation}
        onConfirm={handlePayout}
      />
    </>
  )
}

export default subscribe(
  [DashboardProvider],
  ({
    currentAgencyId,
    agencyName,
    agencyTrading,
    fetchAgencyStatsChart,
    openBankStatement,
    fetchDepositSummaryStats,
  }: any) => ({
    currentAgencyId,
    agencyName,
    agencyTrading,
    fetchAgencyStatsChart,
    openBankStatement,
    fetchDepositSummaryStats,
  }),
)(DashboardPage)
